/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useState,
} from "react";

type AvailableSectionContextValue = {
  isAvailable: (section: string) => boolean;
  setAvailableStatus: (section: string, available: boolean) => void;
};

type AvailableSectionProviderProps = {
  initialAvailableSections?: string[];
  children: ReactNode;
};

const AvailableSectionContext = createContext<AvailableSectionContextValue>(
  {} as AvailableSectionContextValue,
);

export const AvailableSectionProvider = (
  props: AvailableSectionProviderProps,
) => {
  const { initialAvailableSections = [], children } = props;

  const [sections, setSections] = useState<string[]>(initialAvailableSections);

  const isAvailable = useCallback(
    (section: string) => sections.includes(section),
    [sections],
  );

  const setAvailableStatus = useCallback(
    (section: string, available: boolean) => {
      if (available) {
        setSections((prev) => [...new Set([...prev, section])]);
      } else {
        setSections((prev) => prev.filter((option) => option !== section));
      }
    },
    [],
  );

  return (
    <AvailableSectionContext.Provider
      value={{ isAvailable, setAvailableStatus }}
    >
      {children}
    </AvailableSectionContext.Provider>
  );
};

export const useAvailableSection = (): AvailableSectionContextValue => {
  const context = useContext(AvailableSectionContext);

  if (!context) {
    throw new Error("Component beyond AvailableSectionContext");
  }

  return context;
};
